import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Book, 
  ArrowLeft, 
  Hash, 
  Bold, 
  Italic, 
  List, 
  Link, 
  Code, 
  Quote,
  Image,
  Table,
  CheckSquare,
  ExternalLink
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTheme } from '../../hooks/useTheme';

const MarkdownWiki: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('basics');

  const sections = [
    { id: 'basics', title: 'Markdown Basics', icon: Hash },
    { id: 'formatting', title: 'Text Formatting', icon: Bold },
    { id: 'lists', title: 'Lists & Tasks', icon: List },
    { id: 'links', title: 'Links & References', icon: Link },
    { id: 'code', title: 'Code & Syntax', icon: Code },
    { id: 'tables', title: 'Tables & Data', icon: Table },
    { id: 'advanced', title: 'Advanced Features', icon: ExternalLink },
    { id: 'linking', title: 'Note Linking', icon: Link }
  ];

  const content = {
    basics: `# Markdown Basics

Markdown is a lightweight markup language that allows you to format text using simple syntax.

## Headings

Use \`#\` symbols to create headings:

\`\`\`markdown
# Heading 1
## Heading 2
### Heading 3
#### Heading 4
##### Heading 5
###### Heading 6
\`\`\`

## Paragraphs

Simply write text on separate lines to create paragraphs.

This is a paragraph.

This is another paragraph.

## Line Breaks

End a line with two spaces to create a line break  
like this.

Or leave a blank line between paragraphs.`,

    formatting: `# Text Formatting

## Bold and Italic

Make text **bold** using double asterisks or __underscores__.

Make text *italic* using single asterisks or _underscores_.

Make text ***bold and italic*** using triple asterisks.

\`\`\`markdown
**Bold text**
*Italic text*
***Bold and italic***
\`\`\`

## Strikethrough

~~Strikethrough text~~ using double tildes.

\`\`\`markdown
~~Strikethrough text~~
\`\`\`

## Inline Code

Use \`backticks\` to create inline code.

\`\`\`markdown
Use \`backticks\` for inline code
\`\`\`

## Blockquotes

> This is a blockquote
> 
> It can span multiple lines

\`\`\`markdown
> This is a blockquote
> 
> It can span multiple lines
\`\`\``,

    lists: `# Lists & Tasks

## Unordered Lists

Use \`-\`, \`*\`, or \`+\` for bullet points:

- Item 1
- Item 2
  - Nested item
  - Another nested item
- Item 3

\`\`\`markdown
- Item 1
- Item 2
  - Nested item
  - Another nested item
- Item 3
\`\`\`

## Ordered Lists

Use numbers followed by periods:

1. First item
2. Second item
   1. Nested item
   2. Another nested item
3. Third item

\`\`\`markdown
1. First item
2. Second item
   1. Nested item
   2. Another nested item
3. Third item
\`\`\`

## Task Lists

Create checkboxes with \`- [ ]\` and \`- [x]\`:

- [ ] Incomplete task
- [x] Completed task
- [ ] Another incomplete task

\`\`\`markdown
- [ ] Incomplete task
- [x] Completed task
- [ ] Another incomplete task
\`\`\``,

    links: `# Links & References

## Basic Links

Create links using \`[text](URL)\`:

[StudyBro](https://studybro.com)
[Google](https://google.com)

\`\`\`markdown
[StudyBro](https://studybro.com)
[Google](https://google.com)
\`\`\`

## Reference Links

Create reference-style links:

This is a [reference link][1] and another [reference link][studybro].

[1]: https://example.com
[studybro]: https://studybro.com

\`\`\`markdown
This is a [reference link][1] and another [reference link][studybro].

[1]: https://example.com
[studybro]: https://studybro.com
\`\`\`

## Automatic Links

URLs and email addresses can be automatically linked:

https://studybro.com
email@example.com

\`\`\`markdown
https://studybro.com
email@example.com
\`\`\`

## Images

Add images using \`![alt text](URL)\`:

![StudyBro Logo](https://via.placeholder.com/150x50/89b4fa/ffffff?text=StudyBro)

\`\`\`markdown
![StudyBro Logo](https://via.placeholder.com/150x50/89b4fa/ffffff?text=StudyBro)
\`\`\``,

    code: `# Code & Syntax

## Inline Code

Use single backticks for \`inline code\`.

\`\`\`markdown
Use single backticks for \`inline code\`.
\`\`\`

## Code Blocks

Use triple backticks for code blocks:

\`\`\`javascript
function hello() {
  console.log("Hello, World!");
}
\`\`\`

\`\`\`python
def hello():
    print("Hello, World!")
\`\`\`

\`\`\`css
.button {
  background-color: #89b4fa;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
}
\`\`\`

## Syntax Highlighting

Specify the language after the opening backticks:

\`\`\`markdown
\`\`\`javascript
const greeting = "Hello, World!";
console.log(greeting);
\`\`\`
\`\`\`

## Indented Code Blocks

Indent with 4 spaces or 1 tab:

    function hello() {
      return "Hello, World!";
    }`,

    tables: `# Tables & Data

## Basic Tables

Create tables using pipes \`|\`:

| Name | Age | City |
|------|-----|------|
| John | 25 | NYC |
| Jane | 30 | LA |
| Bob | 35 | Chicago |

\`\`\`markdown
| Name | Age | City |
|------|-----|------|
| John | 25 | NYC |
| Jane | 30 | LA |
| Bob | 35 | Chicago |
\`\`\`

## Table Alignment

Use colons to align columns:

| Left | Center | Right |
|:-----|:------:|------:|
| Text | Text | Text |
| More | More | More |

\`\`\`markdown
| Left | Center | Right |
|:-----|:------:|------:|
| Text | Text | Text |
| More | More | More |
\`\`\`

## Complex Tables

Tables can contain formatted text:

| Feature | Status | Notes |
|---------|--------|-------|
| **Bold** | ✅ | *Working* |
| \`Code\` | ✅ | [Link](/) |
| ~~Strike~~ | ❌ | ~Deprecated~ |

\`\`\`markdown
| Feature | Status | Notes |
|---------|--------|-------|
| **Bold** | ✅ | *Working* |
| \`Code\` | ✅ | [Link](/) |
| ~~Strike~~ | ❌ | ~Deprecated~ |
\`\`\``,

    advanced: `# Advanced Features

## Horizontal Rules

Create horizontal lines with three or more hyphens:

---

\`\`\`markdown
---
\`\`\`

## Escape Characters

Use backslashes to escape special characters:

\\*This is not italic\\*
\\[This is not a link\\]

\`\`\`markdown
\\*This is not italic\\*
\\[This is not a link\\]
\`\`\`

## HTML in Markdown

You can use HTML tags in markdown:

<div style="color: red;">This is red text</div>

<details>
<summary>Click to expand</summary>
This content is hidden by default.
</details>

\`\`\`markdown
<div style="color: red;">This is red text</div>

<details>
<summary>Click to expand</summary>
This content is hidden by default.
</details>
\`\`\`

## Math (if supported)

Some markdown processors support LaTeX math:

Inline math: $E = mc^2$

Block math:
$$
\\sum_{i=1}^{n} x_i = x_1 + x_2 + \\cdots + x_n
$$

\`\`\`markdown
Inline math: $E = mc^2$

Block math:
$$
\\sum_{i=1}^{n} x_i = x_1 + x_2 + \\cdots + x_n
$$
\`\`\``,

    linking: `# Note Linking in StudyBro

StudyBro supports powerful note linking features that help you create a connected knowledge base.

## Basic Note Links

Link to other notes using double brackets:

\`\`\`markdown
[[Note Title]]
[[Another Note]]
\`\`\`

This creates clickable links that will:
- Navigate to the linked note if it exists
- Show a preview on hover
- Appear in the graph view as connections

## Alias Links

Create custom link text:

\`\`\`markdown
[[Note Title|Custom Display Text]]
[[React Fundamentals|React Basics]]
\`\`\`

## Section Links

Link to specific sections within notes:

\`\`\`markdown
[[Note Title#Section Name]]
[[React Fundamentals#Components]]
\`\`\`

## Tag Links

Use hashtags to create topic-based connections:

\`\`\`markdown
#react #javascript #frontend
\`\`\`

Tags automatically:
- Create connections between notes with the same tags
- Appear in the graph view
- Enable filtering and search

## Backlinks

StudyBro automatically tracks backlinks:
- See which notes link to the current note
- Navigate bidirectionally between connected notes
- Discover related content

## Graph Visualization

All note links appear in the graph view:
- **Blue nodes**: Regular notes
- **Green nodes**: Projects
- **Yellow nodes**: Documents
- **Lines**: Connections between notes

## Best Practices

### 1. Use Descriptive Titles
\`\`\`markdown
Good: [[React Component Lifecycle Methods]]
Bad: [[Notes 1]]
\`\`\`

### 2. Create Hub Notes
Create index notes that link to related topics:
\`\`\`markdown
# React Learning Path

## Fundamentals
- [[React Components]]
- [[JSX Syntax]]
- [[Props and State]]

## Advanced Topics
- [[React Hooks]]
- [[Context API]]
- [[Performance Optimization]]
\`\`\`

### 3. Use Consistent Tagging
\`\`\`markdown
#programming #web-development #react
\`\`\`

### 4. Link Liberally
Don't hesitate to create links - they help build your knowledge graph:
\`\`\`markdown
When learning [[React]], it's important to understand [[JavaScript]] 
fundamentals, especially [[ES6 Features]] like arrow functions and 
destructuring. The [[Virtual DOM]] concept is also crucial.
\`\`\`

## Examples

### Study Note Example
\`\`\`markdown
# React Hooks

React Hooks were introduced in [[React 16.8]] and allow you to use 
state and other React features in functional components.

## useState Hook
The \`useState\` hook manages local state in functional components.
See [[State Management]] for more details.

## useEffect Hook  
The \`useEffect\` hook handles side effects. Related: [[Component Lifecycle]]

#react #hooks #javascript #frontend
\`\`\`

### Project Note Example
\`\`\`markdown
# Todo App Project

Building a todo app to practice [[React Fundamentals]].

## Features
- [ ] Add todos
- [ ] Mark complete  
- [ ] Delete todos
- [ ] Filter by status

## Technologies
- [[React]] with [[Hooks]]
- [[CSS Modules]] for styling
- [[Local Storage]] for persistence

Related: [[JavaScript Projects]], [[React Learning Path]]

#project #react #javascript #todo-app
\`\`\`

This linking system helps you build a connected knowledge base where ideas and concepts naturally relate to each other, making it easier to discover connections and deepen your understanding.`
  };

  const currentContent = content[activeSection as keyof typeof content];

  return (
    <div className="h-[calc(100vh-8rem)] flex">
      {/* Sidebar */}
      <div 
        className="w-80 border-r overflow-y-auto"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="p-4 border-b" style={{ borderColor: colors.surface0 }}>
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/notes')}
              className="p-2 rounded-lg hover:opacity-80"
              style={{ backgroundColor: colors.surface0 }}
            >
              <ArrowLeft className="w-4 h-4" style={{ color: colors.text }} />
            </button>
            <Book className="w-6 h-6" style={{ color: colors.blue }} />
            <h1 className="text-xl font-bold" style={{ color: colors.text }}>
              Markdown Wiki
            </h1>
          </div>
          <p className="text-sm" style={{ color: colors.subtext0 }}>
            Learn how to write notes in markdown and link them together
          </p>
        </div>
        
        <nav className="p-4">
          <div className="space-y-2">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    isActive ? 'text-white' : 'hover:opacity-80'
                  }`}
                  style={{
                    backgroundColor: isActive ? colors.blue : 'transparent',
                    color: isActive ? 'white' : colors.text
                  }}
                >
                  <Icon className="w-4 h-4" />
                  {section.title}
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 flex">
        {/* Markdown Content */}
        <div className="flex-1 overflow-y-auto">
          <div 
            className="p-8 prose prose-lg max-w-none"
            style={{ color: colors.text }}
          >
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({children}) => <h1 style={{ color: colors.text, borderBottom: `2px solid ${colors.surface0}`, paddingBottom: '0.5rem' }}>{children}</h1>,
                h2: ({children}) => <h2 style={{ color: colors.text, marginTop: '2rem' }}>{children}</h2>,
                h3: ({children}) => <h3 style={{ color: colors.text, marginTop: '1.5rem' }}>{children}</h3>,
                p: ({children}) => <p style={{ color: colors.text, lineHeight: '1.7' }}>{children}</p>,
                strong: ({children}) => <strong style={{ color: colors.text, fontWeight: '600' }}>{children}</strong>,
                em: ({children}) => <em style={{ color: colors.text }}>{children}</em>,
                code: ({children}) => <code style={{ backgroundColor: colors.surface0, color: colors.text, padding: '2px 6px', borderRadius: '4px', fontSize: '0.9em' }}>{children}</code>,
                pre: ({children}) => <pre style={{ backgroundColor: colors.surface0, color: colors.text, padding: '1rem', borderRadius: '8px', overflow: 'auto' }}>{children}</pre>,
                blockquote: ({children}) => <blockquote style={{ borderLeft: `4px solid ${colors.blue}`, paddingLeft: '1rem', margin: '1rem 0', color: colors.subtext0, fontStyle: 'italic' }}>{children}</blockquote>,
                ul: ({children}) => <ul style={{ color: colors.text, paddingLeft: '1.5rem' }}>{children}</ul>,
                ol: ({children}) => <ol style={{ color: colors.text, paddingLeft: '1.5rem' }}>{children}</ol>,
                li: ({children}) => <li style={{ color: colors.text, marginBottom: '0.5rem' }}>{children}</li>,
                table: ({children}) => <table style={{ borderCollapse: 'collapse', width: '100%', margin: '1rem 0' }}>{children}</table>,
                th: ({children}) => <th style={{ border: `1px solid ${colors.surface1}`, padding: '0.75rem', backgroundColor: colors.surface0, color: colors.text, textAlign: 'left' }}>{children}</th>,
                td: ({children}) => <td style={{ border: `1px solid ${colors.surface1}`, padding: '0.75rem', color: colors.text }}>{children}</td>,
                a: ({children, href}) => <a href={href} style={{ color: colors.blue, textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                img: ({src, alt}) => <img src={src} alt={alt} style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', margin: '1rem 0' }} />,
                hr: () => <hr style={{ border: 'none', borderTop: `2px solid ${colors.surface0}`, margin: '2rem 0' }} />
              }}
            >
              {currentContent}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkdownWiki;